<template>
  <Listbox v-model="currentLang" as="div" class="relative">
    <ListboxButton
      class="group inline-flex items-center gap-x-1.5 sm:gap-x-2 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-85 hover:bg-primary-1 transition-colors"
    >
      <GlobeAltIcon
        class="h-4 w-4 sm:h-5 sm:w-5 text-gray-45 group-hover:text-primary-6"
      />
      <span class="hidden sm:block">{{ getCurrentLanguageName() }}</span>
    </ListboxButton>

    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <ListboxOptions
        class="absolute right-0 z-50 mt-2 w-40 sm:w-48 overflow-hidden rounded-xl bg-white shadow-elevated ring-1 ring-black/5"
      >
        <div class="py-1">
          <ListboxOption
            v-for="lang in languages"
            :key="lang.code"
            :value="lang.code"
            v-slot="{ active, selected }"
            as="template"
          >
            <li
              :class="[
                active ? 'bg-primary-1 text-primary-6' : 'text-gray-85',
                'relative flex cursor-default select-none items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5',
              ]"
            >
              <span class="text-base sm:text-lg flex-shrink-0">{{
                lang.flag
              }}</span>
              <span
                :class="[
                  selected ? 'font-semibold' : 'font-normal',
                  'block truncate text-xs sm:text-sm',
                ]"
              >
                {{ lang.name }}
              </span>
              <span
                v-if="selected"
                class="absolute right-3 sm:right-4 flex items-center text-primary-6"
              >
                <CheckIcon class="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
              </span>
            </li>
          </ListboxOption>
        </div>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/vue";
import { CheckIcon } from "@heroicons/vue/20/solid";
import { GlobeAltIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";
import { setLanguage } from "@/i18n";
import type { AvailableLanguages } from "@/i18n";

const { locale } = useI18n();
const currentLang = ref(locale.value);

// 定义可用语言
const languages = [
  {
    code: "zh-CN",
    name: "简体中文",
    nativeName: "Chinese (Simplified)",
    flag: "🇨🇳",
  },
  {
    code: "en-GB",
    name: "English (UK)",
    nativeName: "English",
    flag: "🇬🇧",
  },
];

// 获取当前语言名称
const getCurrentLanguageName = () => {
  const lang = languages.find((l) => l.code === currentLang.value);
  return lang?.name || "English";
};

// 监听语言变化
watch(currentLang, (newLang) => {
  locale.value = newLang as AvailableLanguages;
  setLanguage(newLang as AvailableLanguages);
});
</script>
